/**
 * Rounded label — the `.chip` family from the vanilla pages.
 *
 * Tones map to the ported CSS classes:
 *   neutral → `.chip`, good → `.chip.ok`, warn → `.chip.warn`,
 *   alert → `.chip.kev`, info → `.chip.info`, idle → `.chip.idle`,
 *   accent → `.chip.eu`.
 *
 * Every status tone (good, warn, alert, info, idle) carries a dot — that dot
 * is what marks a chip as a status, so status chips take no icons. `neutral`
 * is a plain label without a dot; `idle` is a status that is neither good nor
 * bad (e.g. „ohne Bewertung“). `accent` is Strong Orange and marks action or
 * selection only — never a status.
 */

import type { ReactNode } from 'react';

export type ChipTone = 'neutral' | 'good' | 'warn' | 'alert' | 'info' | 'idle' | 'accent';

const TONE_CLASS: Readonly<Record<ChipTone, string>> = {
  neutral: '',
  good: 'ok',
  warn: 'warn',
  alert: 'kev',
  info: 'info',
  idle: 'idle',
  accent: 'eu',
};

export interface ChipProps {
  children: ReactNode;
  tone?: ChipTone;
  /** Monospace variant, used for CVE ids. */
  mono?: boolean;
  /** Status as text: dot + coloured label without the tint (secondary status columns). */
  plain?: boolean;
  /** A coloured count rather than a status (e.g. „1 KEV“): tint and text, no dot. */
  count?: boolean;
  /** Native tooltip. */
  title?: string;
  className?: string;
}

export function Chip({ children, tone = 'neutral', mono = false, plain = false, count = false, title, className }: ChipProps): ReactNode {
  const classes = ['chip'];
  const toneClass = TONE_CLASS[tone];
  if (toneClass) classes.push(toneClass);
  if (mono) classes.push('mono');
  if (plain) classes.push('plain');
  if (count) classes.push('count');
  if (className) classes.push(className);
  return (
    <span className={classes.join(' ')} title={title}>
      {children}
    </span>
  );
}

export interface ChipListProps {
  children: ReactNode;
  className?: string;
}

/** Wrapping flex row for several chips (`.chips`). */
export function ChipList({ children, className }: ChipListProps): ReactNode {
  return <div className={className ? `chips ${className}` : 'chips'}>{children}</div>;
}

export default Chip;
